import type { ColumnDef } from "@/components/core/data-table";
import DataTable from "@/components/core/data-table";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { entityType } from "@/constants/entity-types";
import { calendarsQueryOptions } from "@/features/dashboard/api/queries/calendar-queries";
import { eventsQueryOptions } from "@/features/dashboard/api/queries/event-queries";
import type { Event } from "@/features/dashboard/types/event";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/calendar_/selectedDate")({
	component: EventTable,
	validateSearch: z.object({
		calendars: z.array(z.string()).optional(),
		date: z.string().optional(),
	}),
	loaderDeps: ({ search }) => ({
		calendars: search.calendars,
		date: search.date,
	}),
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(
			calendarsQueryOptions({
				entity_type: entityType.USER,
			}),
		);
		return queryClient.ensureQueryData(
			eventsQueryOptions({
				...deps,
				date: deps.date,
			}),
		);
	},
});

function EventTable() {
	const { user } = useAuth();
	const isOcc = user?.is_occ;
	const search = Route.useSearch();
	const calendarsQueries = useSuspenseQuery(
		calendarsQueryOptions({
			entity_type: entityType.USER,
		}),
	);
	const calendars = calendarsQueries.data;

	const eventsQuery = useSuspenseQuery(
		eventsQueryOptions({
			calendars: calendars.data.length !== 0 ? calendars.data.map((cal) => cal.id) : search.calendars,
			date: search.date,
			include: "caseRecord",
		}),
	);
	const events = eventsQuery.data.data;

	const columns: ColumnDef<Event>[] = [
		{
			id: "name",
			label: "Case Title",
			cell: ({ data }) =>
				isOcc ? (
					<Link className="text-blue-600" to="/case-classification/$caseClassificationId" params={{ caseClassificationId: data?.case_record_id || "" }}>
						{data?.caseRecord?.case_title || "n/a"}
					</Link>
				) : (
					<Link className="text-blue-600" to="/case-evaluation/$caseEvaluationId" params={{ caseEvaluationId: data?.case_record_id || "" }}>
						{data?.caseRecord?.case_title || "n/a"}
					</Link>
				),
		},
		{
			id: "case_number",
			label: "Case Number",
			cell: ({ data }) => data?.caseRecord?.case_number || "n/a",
		},
		{
			id: "nature_of_action",
			label: "Nature of Action",
			cell: ({ data }) => data?.caseRecord?.nature_of_action || "n/a",
		},
		{
			id: "hearing_remarks",
			label: "Hearing Remarks",
			cell: ({ data }) => data?.caseActivity?.remarks || "n/a",
		},
		{
			id: "name",
			label: "Date Filed",
			cellClassName: "text-right",
			headerClassName: "text-right",
			cell: ({ data }) => formatDate(data?.caseRecord?.filed_at),
		},
	];

	return (
		<Content pageName="Case Calendar">
			{/*<Header title="Case Calendar" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Case Calendar</span>
						<span className="text-base text-foreground/70">Brief description for help here</span>
					</div>
				</div>
				<div className="flex flex-col w-full gap-4">
					<DataTable showToolbar={false} data={events} columns={columns} showBorder={true} compact={true} />
				</div>
			</div>
		</Content>
	);
}
